import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertView, ClaimRequest } from '@netfoundry-ui/shared/model';
import { isEmpty } from 'lodash';
import { alertTypeLabel } from './alert-type-labels';

/** What the dialog needs to describe the alert it was opened from. */
export interface ClaimAlertDialogData {
    alert: AlertView;
}

/**
 * Takes ownership of one alert, with a note.
 *
 * The note is the whole reason this is a dialog rather than a bare button: a claim on its
 * own says only that somebody has the alert, and the note says what they are doing about it.
 * It goes out in the claim request itself and stays optional, because taking an alert during
 * an incident should not wait on a text box - and nothing else is asked, since who took the
 * alert comes from the caller's token and a claim changes no part of the lifecycle.
 */
@Component({
    selector: 'app-claim-alert-dialog',
    templateUrl: './claim-alert-dialog.component.html',
    /* The mute dialog's stylesheet, shared rather than copied: the two are the same
     * surface with the same labels, notes and buttons, and Angular scopes the rules to
     * each component separately. */
    styleUrls: ['./mute-alert-dialog.component.scss'],
    standalone: false,
})
export class ClaimAlertDialogComponent {
    readonly alert: AlertView;

    note = '';

    constructor(
        private dialogRef: MatDialogRef<ClaimAlertDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: ClaimAlertDialogData
    ) {
        this.alert = data.alert;
    }

    /**
     * The alert in plain words, so the reader can see what they are taking.
     *
     * The readable type name, the same one the table and the drawer show, and the
     * component's name when the network's controller could answer for it. The id is the
     * fallback rather than a placeholder: it is always on the alert, and it is something
     * a reader can act on.
     */
    get alertSummary(): string {
        const type = alertTypeLabel(this.alert.alertType);
        const component = this.alert.componentName || this.alert.componentId;
        return isEmpty(component) ? type : `${type} on ${component}`;
    }

    cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Closes with the request to send. A blank note is left off the request rather than
     * sent as an empty string, so the alert carries no note at all instead of an empty
     * one. Cancel closes with `undefined`, which is how the caller tells the two apart.
     */
    confirm(): void {
        const note = this.note.trim();
        const request: ClaimRequest = { note: isEmpty(note) ? undefined : note };
        this.dialogRef.close(request);
    }
}
